import React, { useEffect, useMemo, useRef } from 'react';
import { ImageOption } from '../../common/CommonTypes';
import {
    AlloyWheelRepairRequest,
    AlloyWheelSpecificErrors,
    DamageSizeOption,
    DamageTypes,
    DamageSizes
} from './AlloyRepairRequestTypes';
import SelectableImageMultiple from '../../common/SelectableImageMultiple';
import ImageUploader from '../../common/fileUpload/ImageUploader';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import DateInput from '../../common/dateInput/DateInput';
import SelectInput from '../../common/selectInput/SelectInput';
import TextArea from '../../common/textArea/TextArea';
import FormInput from '../../common/FormInput';
import Images from '../../../images/AlloySpecificFormImages';
import { useTranslation } from 'react-i18next';
import { TranslationHelper } from 'classes/TranslationHelper';
import { FeatureState } from '../../../Constants/Common';
import { isAllowedInLocation, nonUsLocations } from '../../../helpers/location';
import { useAppSelector } from 'state/hooks';
import { selectIsUSSite, selectSiteCountry } from 'state/selectors/site';

export type AlloySpecificFormProps = {
    repairRequest: AlloyWheelRepairRequest;
    formErrors?: AlloyWheelSpecificErrors;
    onChange: (key: string, value: string) => void;
    onChangeDamageType: (selectionName: string) => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    errorSummaryTitle: string;
};

const AlloySpecificForm = ({
    repairRequest,
    formErrors,
    onChange,
    onChangeDamageType,
    onUploadImageChange,
    errorSummaryTitle
}: AlloySpecificFormProps): JSX.Element => {
    const features = useAppSelector((s) => s.site.features);
    const isUsSite = useAppSelector(selectIsUSSite);
    const siteLocation = useAppSelector(selectSiteCountry);
    const { t, i18n } = useTranslation();
    const onChangeEvent = (selectedValue: string, key: string) => {
        onChange(key, selectedValue);
    };

    const modalBottom = useRef(null);
    const uploadImageLabel = isUsSite
        ? t('alloy_repair_request.step_2_upload_image_3feet_away')
        : t('alloy_repair_request.step_2_upload_image_1m_away');
    const distanceImageLabel = isUsSite
        ? t(
              'alloy_repair_request.step_2_damage_upload_labels.image_from_3feet_away'
          )
        : t(
              'alloy_repair_request.step_2_damage_upload_labels.image_from_1m_away'
          );

    const helpTextDistance = t('help_text.distance');

    useEffect(() => {
        if (formErrors !== undefined && Object.keys(formErrors).length !== 0) {
            modalBottom.current.focus();
        }
    }, [formErrors]);

    const damageTypeOptions = useMemo(() => {
        const allDamageTypeOptions: ImageOption[] = [
            {
                name: DamageTypes.Scratch,
                image: Images.AlloyWheelScratch,
                altText: DamageTypes.Scratch,
                label: t('scratch')
            },
            {
                name: DamageTypes.Scuff,
                image: Images.AlloyWheelScuff,
                altText: DamageTypes.Scuff,
                label: t('scuff')
            },
            {
                name: DamageTypes.Corroded,
                image: Images.AlloyWheelCorroded,
                altText: DamageTypes.Corroded,
                label: t('corroded')
            },
            {
                name: DamageTypes.Cracked,
                image: Images.AlloyWheelCracked,
                altText: DamageTypes.Cracked,
                label: t('cracked'),
                countries: nonUsLocations
            },
            {
                name: DamageTypes.Buckled,
                image: Images.AlloyWheelBuckled,
                altText: DamageTypes.Buckled,
                label: t('buckled'),
                countries: nonUsLocations
            }
        ];

        return allDamageTypeOptions.filter((o) => {
            const allowedInLocation = isAllowedInLocation(
                o.countries,
                siteLocation
            );

            const isFeatureFlagDisabled =
                features.WheelDamage === FeatureState.Off;
            const isHandledByFeatureFlag =
                o.name === DamageTypes.Cracked ||
                o.name === DamageTypes.Buckled;

            return (
                allowedInLocation &&
                (!isFeatureFlagDisabled || !isHandledByFeatureFlag)
            );
        });
    }, [i18n.language]);

    const damageSizeOptions: DamageSizeOption[] = [
        {
            id: DamageSizes.OneQuarter,
            text: TranslationHelper.getTranslatedText(DamageSizes.OneQuarter),
            value: DamageSizes.OneQuarter,
            disabled: false
        },
        {
            id: DamageSizes.TwoQuarters,
            text: TranslationHelper.getTranslatedText(DamageSizes.TwoQuarters),
            value: DamageSizes.TwoQuarters,
            disabled: false
        },
        {
            id: DamageSizes.ThreeQuarters,
            text: TranslationHelper.getTranslatedText(
                DamageSizes.ThreeQuarters
            ),
            value: DamageSizes.ThreeQuarters,
            disabled: false
        },
        {
            id: DamageSizes.FourQuarters,
            text: TranslationHelper.getTranslatedText(DamageSizes.FourQuarters),
            value: DamageSizes.FourQuarters,
            disabled: false
        }
    ];

    const errorlist = (): Array<string | undefined> => {
        const values = [
            formErrors?.damagedWheel,
            formErrors?.dateOfIncident,
            formErrors?.damageType,
            formErrors?.damageSize,
            formErrors?.closeUpImage,
            formErrors?.distanceImage
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    const maxDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate());
        return dt;
    };

    return (
        <div className='card page-content no-border'>
            <div className='card-body page-content-body'>
                <FormInput
                    id='incident-date-forminput'
                    label={t('alloy_repair_request.date_of_incident')}
                    helpText={t('help_text.date_of_incident')}
                    errorText={formErrors?.dateOfIncident}>
                    <DateInput
                        name='Date of incident'
                        useJson
                        forceTimeReset
                        value={repairRequest?.dateOfIncident}
                        onDateChanged={(dateString) =>
                            onChangeEvent(dateString, 'dateOfIncident')
                        }
                        maximumDate={maxDate()}
                    />
                </FormInput>
                <FormInput
                    id='damage-type-forminput'
                    label={t(
                        'alloy_repair_request.step_2_damage_type_selection_title'
                    )}
                    helpText={t('help_text.type_of_damage')}
                    errorText={formErrors?.damageType}>
                    <SelectableImageMultiple
                        imageOptions={damageTypeOptions}
                        selectedItem={repairRequest?.damageType}
                        onChange={onChangeDamageType}
                    />
                </FormInput>
                <FormInput
                    id='damage-size-forminput'
                    label={t('alloy_repair_request.step_2_damage_count_title')}
                    helpText={t('help_text.size_of_damage')}
                    errorText={formErrors?.damageSize}>
                    <SelectInput
                        name='damage-size'
                        loadingText={t('loading')}
                        value={repairRequest?.damageSize}
                        options={damageSizeOptions}
                        onChange={(selectedValue: string) =>
                            onChangeEvent(selectedValue, 'damageSize')
                        }
                        forceDefault
                        defaultIfOneOption
                        addDefaultOption
                        defaultText={t('select_default')}
                    />
                </FormInput>
                <FormInput
                    id='closeup-image-forminput'
                    label={t(
                        'alloy_repair_request.step_2_damage_upload_photo_title'
                    )}
                    helpText={t('help_text.close_up')}
                    errorText={formErrors?.closeUpImage}>
                    <div className='images-wrapper'>
                        <div className='image-wrapper'>
                            <p
                                className='example-image-text'
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}>
                                {t(
                                    'alloy_repair_request.step_2_damage_upload_labels.example'
                                )}
                            </p>
                            <img
                                className='example-images'
                                id='close-up-example-images'
                                src={Images.AlloyWheelCloseup}
                                alt='close up image of alloy wheel damage'
                            />
                        </div>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {t(
                                        'alloy_repair_request.step_2_damage_upload_labels.close_up_image'
                                    )}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='close-up-damage-preview'
                                        title={t('upload_image')}
                                        UploadedImage={
                                            repairRequest?.uploadedImages[0]
                                        }
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='distance-image-forminput'
                    label={uploadImageLabel}
                    helpText={helpTextDistance}
                    errorText={formErrors?.distanceImage}>
                    <div className='images-wrapper'>
                        <div className='image-wrapper'>
                            <p
                                className='example-image-text'
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}>
                                {t(
                                    'alloy_repair_request.step_2_damage_upload_labels.example'
                                )}
                            </p>
                            <img
                                className='example-images'
                                id='damage-example-images'
                                src={Images.AlloyWheelDistance}
                                alt='distance image of alloy wheel damage'
                            />
                        </div>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {distanceImageLabel}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='distance-damage-preview'
                                        title={t('upload_image')}
                                        UploadedImage={
                                            repairRequest?.uploadedImages[1]
                                        }
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='additional-image-forminput'
                    label={t(
                        'alloy_repair_request.step_2_additional_images_title'
                    )}
                    helpText={t('help_text.additional_images')}>
                    <div className='images-wrapper'>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {t(
                                        'alloy_repair_request.step_2_damage_upload_labels.additional_image1_optional'
                                    )}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='additional-image-one-preview'
                                        title={t('upload_image')}
                                        UploadedImage={
                                            repairRequest?.uploadedImages[2]
                                        }
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='image-wrapper'>
                            <div className='image-preview'>
                                <div className='image-preview-header'>
                                    {t(
                                        'alloy_repair_request.step_2_damage_upload_labels.additional_image2_optional'
                                    )}
                                </div>
                                <div className='image-preview-content'>
                                    <ImageUploader
                                        name='additional-image-two-preview'
                                        title={t('upload_image')}
                                        UploadedImage={
                                            repairRequest?.uploadedImages[3]
                                        }
                                        onImageChange={onUploadImageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </FormInput>
                <FormInput
                    id='other-details-forminput'
                    label={t('alloy_repair_request.step_2_any_other_details')}>
                    <TextArea
                        name='additional_information'
                        rows={3}
                        value={repairRequest?.additionalInformation}
                        onChange={(e: React.FormEvent<HTMLTextAreaElement>) =>
                            onChangeEvent(
                                e.currentTarget.value,
                                'additionalInformation'
                            )
                        }
                        innerClassName='fullscreen'
                    />
                </FormInput>
            </div>
            <div className='card-footer page-content-footer'>
                {formErrors && (
                    <ErrorSummary
                        title={errorSummaryTitle}
                        errors={errorlist()}
                        className='card-error'
                    />
                )}
            </div>
            <div tabIndex={0} ref={modalBottom}></div>
        </div>
    );
};

export default AlloySpecificForm;
